import { toCamelCase, toCaseCompatible } from '@server/shared/utils/case-adapter'
import { z } from 'zod'

import { SubscribeTicket, rawSubscribeTicketSchema, type GetRawSubscribeTicketDetailResponse } from '../dto'

export const subscribeTicketAdapter = (rawSubscribeTicket: unknown): SubscribeTicket => {
  const t = toCamelCase<SubscribeTicket>(rawSubscribeTicket)

  if (
    t.productId &&
    (t.currency !== 'KRW' ||
      typeof t.paymentAmount !== 'number' ||
      !Number.isSafeInteger(t.paymentAmount) ||
      t.paymentAmount < 0 ||
      typeof t.finalPaymentAmount !== 'number' ||
      !Number.isSafeInteger(t.finalPaymentAmount) ||
      t.finalPaymentAmount < 0 ||
      typeof t.finalPrice !== 'number' ||
      !Number.isSafeInteger(t.finalPrice) ||
      t.finalPrice < 0)
  ) {
    throw new Error('Invalid KR product catalog pricing')
  }

  return {
    id: t.id,
    productId: t.productId,
    currency: t.currency,
    paymentAmount: t.paymentAmount,
    finalPaymentAmount: t.finalPaymentAmount,
    viewPrice: t.viewPrice,
    viewDiscountRate: t.viewDiscountRate,
    finalPrice: t.finalPrice,
    finalDiscountRate: t.finalDiscountRate,
    appliedCoupon: t.appliedCoupon,
    productKind: t.productKind,
    countryCode: t.countryCode,
    totalLessonCount: t.totalLessonCount,
    validityMonths: t.validityMonths,
    paymentType: t.paymentType,
    langType: t.langType,
    subName: t.subName,
    lessonMonth: t.lessonMonth,
    lessonCountPerMonth: t.lessonCountPerMonth,
    originPrice: t.originPrice,
    subPrice: t.subPrice,
    discountRate: t.discountRate,
    lessonTime: t.lessonTime,
    creDateTime: t.creDateTime,
    isBest: false,
    curriculumType: t.curriculumType ?? 'BASIC',
    promotionType: t.promotionType ?? 'NONE',
    description: t.description ?? [],
    eventDescription: t.eventDescription ?? [],
  } as SubscribeTicket
}

// ProductTargetDTO is persisted with the order. Only fields actually present in that snapshot override metadata.
export const frozenSubscribeTargetSchema = z.object({
  id: z.string(),
  targetType: z.literal('SUBSCRIBE').optional(),
  name: z.string().nullable().optional(),
  productKind: rawSubscribeTicketSchema.shape.product_kind,
  countryCode: rawSubscribeTicketSchema.shape.country_code,
  totalLessonCount: rawSubscribeTicketSchema.shape.total_lesson_count,
  validityMonths: rawSubscribeTicketSchema.shape.validity_months,
  langType: rawSubscribeTicketSchema.shape.lang_type.nullable().optional(),
  paymentType: rawSubscribeTicketSchema.shape.payment_type.nullable().optional(),
  lessonMonth: rawSubscribeTicketSchema.shape.lesson_month.nullable().optional(),
  lessonCountPerMonth: rawSubscribeTicketSchema.shape.lesson_count_per_month.nullable().optional(),
  lessonTime: rawSubscribeTicketSchema.shape.lesson_time.nullable().optional(),
  originPrice: z.number().nullable().optional(),
  salePrice: z.number().nullable().optional(),
})

export function applyFrozenSubscribeTarget(
  ticket: GetRawSubscribeTicketDetailResponse,
  snapshot: unknown,
): GetRawSubscribeTicketDetailResponse {
  const target = frozenSubscribeTargetSchema.parse(snapshot)
  if (target.id !== ticket.id) {
    throw new Error('KR_PAYMENT_ORDER_MISMATCH')
  }
  const frozen = {
    ...(target.name !== null && target.name !== undefined ? { sub_name: target.name } : {}),
    ...(target.productKind !== undefined ? { product_kind: target.productKind } : {}),
    ...(target.countryCode !== undefined ? { country_code: target.countryCode } : {}),
    ...(target.totalLessonCount !== undefined ? { total_lesson_count: target.totalLessonCount } : {}),
    ...(target.validityMonths !== undefined ? { validity_months: target.validityMonths } : {}),
    ...(target.langType !== null && target.langType !== undefined ? { lang_type: target.langType } : {}),
    ...(target.paymentType !== null && target.paymentType !== undefined ? { payment_type: target.paymentType } : {}),
    ...(target.lessonMonth !== null && target.lessonMonth !== undefined ? { lesson_month: target.lessonMonth } : {}),
    ...(target.lessonCountPerMonth !== null && target.lessonCountPerMonth !== undefined
      ? { lesson_count_per_month: target.lessonCountPerMonth }
      : {}),
    ...(target.lessonTime !== null && target.lessonTime !== undefined ? { lesson_time: target.lessonTime } : {}),
    ...(target.originPrice !== null && target.originPrice !== undefined ? { origin_price: target.originPrice } : {}),
    ...(target.salePrice !== null && target.salePrice !== undefined ? { sub_price: target.salePrice } : {}),
  }
  return { ...ticket, ...toCaseCompatible<typeof frozen>(frozen) }
}
